import { useEffect, useMemo, useRef, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { format } from 'date-fns';
import { Add } from '@mui/icons-material';
import { Autocomplete, Button, darken, debounce, Divider, Grid, IconButton, lighten, styled, TextField, Typography } from '@mui/material';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { usePerfilService } from '../../../../../use-cases/perfil';
import { IPerfil } from '../../../../../application/models/IPerfil';
import { SkeletonVelesTable } from '../../../../components/skeleton-veles';
import EditIcon from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility'
import { useClientesService } from '../../../../../use-cases/clientes';
import { ICliente, IClienteTable, ITipoUsuario } from '../../../../../application/models';
import { buildClienteTextSearchFiltersFromTerm } from '../../../../../utils/cliente-busca-unificada';

const ListaDePerfis = () => {
  // State
  const { currentUser } = useAppSelector((state) => state.user)
  const { clientes } = useAppSelector((state) => state.clientes)
  const { clienteSelecionado, perfis, perfisTotal, searchPerfilNome } = useAppSelector((state) => state.perfil)

  // Useful variables
  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE

  const canEdit = isDemais || isTecnologiaDaInformacao || isCliente
  const canCreate = isDemais || isTecnologiaDaInformacao || isCliente
  // Use State
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5
  });
  const lastFetchedClienteIdRef = useRef<number | null>(null)
  const isNavigatingToCreateRef = useRef(false)
  // Service
  const { setClientes, setClientesPagination, resetClientesList } = useClientesService()
  const {
    setOpenCreatePerfilForm,
    setClienteSelecionado,
    resetClienteSelecionado,
    getPerfisByIdCliente,
    setOpenEditPerfilForm,
    setEditPerfilForm,
    setEditPerfilFormIdCliente,
    setPerfisPagination,
    resetPerfis,
    setSearchPerfilNome,
    resetSearchPerfilNome
  } = usePerfilService()

  useEffect(() => {
    resetClienteSelecionado()
    resetPerfis()
    resetSearchPerfilNome()
    resetClientesList()
    lastFetchedClienteIdRef.current = null

    return () => {

      if (!isNavigatingToCreateRef.current) {
        resetClienteSelecionado()
      }
      resetPerfis()
      resetSearchPerfilNome()
      lastFetchedClienteIdRef.current = null
      isNavigatingToCreateRef.current = false
    }
  }, [])

  useEffect(() => {
    if (currentUser && isCliente && !clienteSelecionado && clientes.length > 0) {
      const clienteDoUsuario = clientes.find(c => Number(c.IdCliente) === Number(currentUser.IdCliente))

      if (clienteDoUsuario) {
        setClienteSelecionado({
          cliente: {
            Ativo: clienteDoUsuario.Ativo,
            Atualizacao: clienteDoUsuario.Atualizacao,
            CNPJ: clienteDoUsuario.CNPJ,
            IdCliente: clienteDoUsuario.IdCliente,
            Inclusao: clienteDoUsuario.Inclusao,
            LoginCliente: clienteDoUsuario.LoginCliente,
            NomeFantasia: clienteDoUsuario.NomeFantasia,
            RazaoSocial: clienteDoUsuario.RazaoSocial
          }
        })
      }
    }
  }, [currentUser, isCliente, clienteSelecionado, clientes, setClienteSelecionado])

  useEffect(() => {
    if (!clienteSelecionado) {
      lastFetchedClienteIdRef.current = null
      resetPerfis()
      return
    }

    if (isCliente && Number(clienteSelecionado.IdCliente) !== Number(currentUser?.IdCliente)) {
      lastFetchedClienteIdRef.current = null
      resetPerfis()
      return
    }

    const idCliente = clienteSelecionado.IdCliente
    if (lastFetchedClienteIdRef.current === idCliente) return
    lastFetchedClienteIdRef.current = idCliente
    getPerfisByIdCliente()
  }, [clienteSelecionado, currentUser, isCliente, getPerfisByIdCliente, resetPerfis])

  useEffect(() => {
    setClientesPagination({
      limit: 50,
      page: 1
    })

    if (currentUser) {
      if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: currentUser.IdUsuario,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL || currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE) {
        setClientes({
          SearchCNPJ: '',
          SearchRazaoSocial: '',
          SearchNomeFantasia: '',
          SearchLoginCliente: '',
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: currentUser.IdCliente
        });
      }
    }
  }, [setClientes, currentUser, setClientesPagination]);

  const debounceSearchTerm = debounce((searchTerm: string) => {
    if (currentUser) {
      const textFilters = buildClienteTextSearchFiltersFromTerm(searchTerm)

      if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL) {
        setClientes({
          ...textFilters,
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: currentUser.IdUsuario,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL || currentUser.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE) {
        setClientes({
          ...textFilters,
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: 0
        });
      } else if (currentUser.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE) {
        setClientes({
          ...textFilters,
          SearchInclusaoMaxima: undefined,
          SearchInclusaoMinima: undefined,
          SearchIdUsuario: currentUser.IdUsuario,
          searchIdVendedor: 0,
          SearchIdCliente: currentUser.IdCliente
        });
      }
    }
  }, 700)

  const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === 'light'
        ? lighten(theme.palette.primary.light, 0.85)
        : darken(theme.palette.primary.main, 0.8),
  }));

  const GroupItems = styled('ul')({
    padding: 0,
  });

  const columns = useMemo<MRT_ColumnDef<IPerfil>[]>(() => [
    {
      header: canEdit ? 'Editar' : 'Visualizar',
      size: 50,
      Cell: ({ row }) => (
        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            setEditPerfilForm({
              idPerfil: row.original.IdPerfil,
              nome: row.original.Descricao,
              status: row.original.Ativo
            })

            setEditPerfilFormIdCliente(row.original.IdCliente)

            setOpenEditPerfilForm({
              open: true
            })
          }}
        >
          {
            canEdit ? <EditIcon />
              : <Visibility />
          }
        </IconButton>
      ),
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
    },
    {
      accessorKey: 'Descricao',
      header: 'Perfil',
      size: 150,
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
    },
    {
      accessorKey: 'Inclusao',
      header: 'Data de Inclusão',
      size: 150,
      accessorFn: (rowData: IPerfil) => {
        return new Date(rowData.Inclusao);
      },
      Cell: ({ cell }) => <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>,
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
    },
    {
      accessorKey: 'Atualizacao',
      header: 'Data de Atualização',
      size: 150,
      accessorFn: (rowData: IPerfil) => {
        return rowData.Atualizacao ? new Date(rowData.Atualizacao) : null
      },
      Cell: ({ cell }) => {
        if (cell.row.original.Atualizacao) {
          return <span>{format(cell.getValue<Date>(), 'dd/MM/yyyy')}</span>
        }
        return null;
      },
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
    },
    {
      accessorKey: 'Ativo',
      header: 'Status',
      size: 100,
      accessorFn: (rowData: IPerfil) => {
        return rowData.Ativo ? 'Ativo' : 'Inativo';
      },
      Cell: ({ cell }) => {
        const isActive = cell.getValue<string>() === 'Ativo'

        return (
          <Typography
            color="Background"
            variant='caption'
            style={{
              display: 'inline-block',
              padding: '4px 8px',
              borderRadius: '8px',
              fontWeight: 'bold',
              backgroundColor: isActive ? 'green' : 'red',
              textAlign: 'center'
            }}
          >
            {isActive ? 'Ativo' : 'Inativo'}
          </Typography>
        )
      }
    }
  ], [canEdit, setEditPerfilForm, setEditPerfilFormIdCliente, setOpenEditPerfilForm]);

  const perfisFiltrados = useMemo(() => {
    if (!searchPerfilNome || searchPerfilNome.trim() === '') {
      return perfis
    }
    const searchLower = searchPerfilNome.toLowerCase()
    return perfis.filter(perfil =>
      perfil.Descricao?.toLowerCase().includes(searchLower)
    )
  }, [perfis, searchPerfilNome])

  const table = useMaterialReactTable({
    columns,
    data: perfisFiltrados,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enableExpandAll: false,
    enableDensityToggle: false,
    localization: MRT_Localization_PT_BR,
    rowCount: perfisFiltrados.length,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading: loading
    },
    manualPagination: false
  });

  useEffect(() => {
    setLoading(false)
  }, [])

  useEffect(() => {
    setPerfisPagination({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize
    });
  }, [pagination.pageIndex, pagination.pageSize, setPerfisPagination])

  return (
    loading ? <SkeletonVelesTable /> : <>
      <Typography color="primary" fontWeight="bold" variant="h6" gutterBottom>
        Perfis
      </Typography>
      <Divider style={{ margin: '16px 0', borderBottomWidth: '2px', borderBottomColor: 'rgba(0,0,0,0.5)' }} />

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          {canCreate && (
            <Button
              sx={{
                marginBottom: '10px'
              }}
              disabled={!clienteSelecionado}
              variant="contained"
              color="primary"
              onClick={() => {
                isNavigatingToCreateRef.current = true
                setOpenCreatePerfilForm({
                  open: true
                })
                resetClientesList()
              }}
            >
              <Add style={{ marginRight: '8px' }} /> Novo Perfil
            </Button>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            fullWidth
            autoComplete
            includeInputInList
            disabled={isCliente}
            filterOptions={(options) => options}
            value={clienteSelecionado ?? null}
            isOptionEqualToValue={(option, value) => option?.IdCliente === value?.IdCliente}
            options={[...clientes].sort((a, b) => {
              const firstLetterA = (a.LoginCliente?.[0] || '').toUpperCase();
              const firstLetterB = (b.LoginCliente?.[0] || '').toUpperCase();

              if (firstLetterA !== firstLetterB) {
                return firstLetterA.localeCompare(firstLetterB);
              }

              return (a.LoginCliente || '').localeCompare(b.LoginCliente || '');
            })}
            groupBy={(option) => (option.LoginCliente?.[0] || '').toUpperCase()}
            getOptionLabel={(option) => `${option.RazaoSocial} - ${option.LoginCliente}`}
            renderGroup={(params) => (
              <li key={params.key}>
                <GroupHeader>{params.group}</GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )}
            onInputChange={(event, value, reason) => {
              if (reason === 'input' && !isCliente) {
                debounceSearchTerm(value)
              }
            }}
            onChange={(event: any, newValue: IClienteTable | ICliente | null) => {
              if (!isCliente) {
                if (newValue) {
                  setClienteSelecionado({
                    cliente: {
                      Ativo: newValue.Ativo,
                      Atualizacao: newValue.Atualizacao,
                      CNPJ: newValue.CNPJ,
                      IdCliente: newValue.IdCliente,
                      Inclusao: newValue.Inclusao,
                      LoginCliente: newValue.LoginCliente,
                      NomeFantasia: newValue.NomeFantasia,
                      RazaoSocial: newValue.RazaoSocial
                    }
                  })
                } else {
                  resetClienteSelecionado()
                }
              }
            }}
            renderInput={(params) => <TextField {...params} label={isCliente ? "Cliente" : "Selecione um cliente"} placeholder={isCliente ? "Cliente selecionado automaticamente" : "Login, razão social, nome fantasia ou CNPJ"} />}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Buscar perfil por nome"
            variant="outlined"
            value={searchPerfilNome}
            onChange={(e) => {
              const value = e.target.value;
              setSearchPerfilNome({ nome: value });
            }}
            placeholder="Digite o nome do perfil..."
            disabled={!clienteSelecionado}

          />
        </Grid>
      </Grid>

      <MaterialReactTable table={table} />
    </>
  );
};

export { ListaDePerfis };